import { ethers } from 'ethers';

export type InputVariant = 'default' | 'wei';

export interface InputTypeConfig {
  placeholder: (fieldName: string, variant?: InputVariant) => string;
  validate: (value: string, variant?: InputVariant) => ValidationResult;
  transform: (value: string, variant?: InputVariant) => any;
}

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export const InputTypes: Record<string, InputTypeConfig> = {
  address: {
    placeholder: (fieldName: string) =>
      `Enter an Ethereum address for ${fieldName}, e.g., 0x1234...abcd`,
    validate: (value: string) => {
      const isValid = ethers.isAddress(value.trim());
      return {
        isValid,
        error: isValid ? undefined : 'Invalid Ethereum address',
      };
    },
    transform: (value: string) => value.trim(),
  },

  'address[]': {
    placeholder: (fieldName: string) =>
      `Enter multiple Ethereum addresses for ${fieldName} separated by commas`,
    validate: (value: string) => {
      const addresses = value.split(',').map((addr) => addr.trim());
      const validAddresses = addresses.every((addr) => ethers.isAddress(addr));
      return {
        isValid: validAddresses,
        error: validAddresses ? undefined : 'One or more addresses are invalid',
      };
    },
    transform: (value: string) => value.split(',').map((addr) => addr.trim()),
  },

  string: {
    placeholder: (fieldName: string, variant: InputVariant = 'default') => {
      return `Enter text for ${fieldName}`;
    },
    validate: (value: string, variant: InputVariant = 'default') => {
      return { isValid: true };
    },
    transform: (value: string) => value.trim(),
  },

  bool: {
    placeholder: (fieldName: string) => `Enter true or false for ${fieldName}`,
    validate: (value: string) => {
      const normalized = value.toLowerCase().trim();
      switch (normalized) {
        case 'true':
        case 'false':
          return { isValid: true };
        default:
          return { isValid: false, error: 'Must be true or false' };
      }
    },
    transform: (value: string) => value.toLowerCase().trim() === 'true',
  },

  tuple: {
    placeholder: (fieldName: string) => `Enter comma-separated values for ${fieldName}`,
    validate: (value: string) => {
      const hasValues = value.trim().length > 0;
      return {
        isValid: hasValues,
        error: hasValues ? undefined : 'Must provide at least one value',
      };
    },
    transform: (value: string) => value.split(',').map((s) => s.trim()),
  },

  'bytes[]': {
    placeholder: (fieldName: string) =>
      `Enter multiple hex strings for ${fieldName}, separated by commas`,
    validate: (value: string) => {
      try {
        const bytesArray = value.split(',').map((v) => v.trim());
        bytesArray.forEach((v) => ethers.getBytes(v));
        return { isValid: true };
      } catch (error) {
        return { isValid: false, error: 'One or more hex strings are invalid' };
      }
    },
    transform: (value: string) => value.split(',').map((s) => ethers.getBytes(s)),
  },

  bytes32: {
    placeholder: (fieldName: string, variant: InputVariant = 'default') => {
      return `Enter a hex string for ${fieldName} (64 characters)`;
    },
    validate: (value: string) => {
      return ethers.encodeBytes32String(value)
        ? { isValid: true }
        : {
            isValid: false,
            error: 'Must be a valid 32-byte hex string (0x + 64 characters)',
          };
    },
    transform: (value: string) => ethers.encodeBytes32String(value),
  },

  'bytes32[]': {
    placeholder: (fieldName: string) =>
      `Enter multiple 32-byte hex strings for ${fieldName}, separated by commas`,
    validate: (value: string) => {
      const hexStrings = value.split(',').map((s) => s.trim());
      const hexPattern = /^0x[0-9a-fA-F]{64}$/;
      const validHex = hexStrings.every((hex) => hexPattern.test(hex));
      return {
        isValid: validHex,
        error: validHex
          ? undefined
          : 'Each value must be a valid 32-byte hex string (0x + 64 characters)',
      };
    },
    transform: (value: string) =>
      value.split(',').map((s) => ethers.encodeBytes32String(s)),
  },

  int256: {
    placeholder: (fieldName: string, variant: InputVariant = 'default') => {
      switch (variant) {
        case 'wei':
          return `Enter amount in Karrat for ${fieldName} (can be negative)`;
        default:
          return `Enter a number for ${fieldName} (can be negative)`;
      }
    },
    validate: (value: string, variant: InputVariant = 'default') => {
      const num = Number(value);
      if (isNaN(num)) {
        return { isValid: false, error: 'Must be a valid number' };
      }

      switch (variant) {
        case 'wei':
          return value.match(/^-?\d*\.?\d*$/)
            ? { isValid: true }
            : { isValid: false, error: 'Must be a valid Karrat amount' };
        default:
          return { isValid: true };
      }
    },
    transform: (value: string, variant: InputVariant = 'default') => {
      switch (variant) {
        case 'wei':
          return value.startsWith('-')
            ? ethers.parseEther(value.slice(1)) * -1n
            : ethers.parseEther(value);
        default:
          return ethers.toBigInt(value);
      }
    },
  },

  uint8: {
    placeholder: (fieldName: string, variant: InputVariant = 'default') =>
      `Enter a number between 0 and 255 for ${fieldName}`,
    validate: (value: string, variant: InputVariant = 'default') => {
      const num = Number(value);
      if (isNaN(num) || num < 0) {
        return { isValid: false, error: 'Must be a valid positive number' };
      }
      return num <= 255
        ? { isValid: true }
        : { isValid: false, error: 'Number must be between 0 and 255' };
    },
    transform: (value: string) => ethers.toBigInt(value),
  },

  uint256: {
    placeholder: (fieldName: string, variant: InputVariant = 'default') => {
      switch (variant) {
        case 'wei':
          return `Enter amount in Karrat for ${fieldName}`;
        default:
          return `Enter a number for ${fieldName}`;
      }
    },
    validate: (value: string, variant: InputVariant = 'default') => {
      const num = Number(value);
      if (isNaN(num) || num < 0) {
        return { isValid: false, error: 'Must be a valid positive number' };
      }

      switch (variant) {
        case 'wei':
          return value.match(/^\d*\.?\d+$|^\d+$/)
            ? { isValid: true }
            : { isValid: false, error: 'Must be a valid Karrat amount' };
        default:
          return { isValid: true };
      }
    },
    transform: (value: string, variant: InputVariant = 'default') => {
      switch (variant) {
        case 'wei':
          if (!value.match(/^\d*\.?\d+$|^\d+$/)) return '';
          return ethers.parseEther(value);
        default:
          return value === '' ? '' : ethers.toBigInt(value);
      }
    },
  },

  'uint256[]': {
    placeholder: (fieldName: string, variant: InputVariant = 'default') => {
      switch (variant) {
        case 'wei':
          return `Enter multiple Karrat amounts for ${fieldName}, separated by commas`;
        default:
          return `Enter multiple numbers for ${fieldName}, separated by commas`;
      }
    },
    validate: (value: string, variant: InputVariant = 'default') => {
      const values = value.split(',').map((v) => v.trim());
      const allValid = values.every((val) => {
        const num = Number(val);
        if (isNaN(num) || num < 0) return false;

        switch (variant) {
          case 'wei':
            return val.match(/^\d*\.?\d*$/);
          default:
            return true;
        }
      });

      switch (variant) {
        case 'wei':
          return {
            isValid: allValid,
            error: allValid ? undefined : 'All values must be valid Karrat amounts',
          };
        default:
          return {
            isValid: allValid,
            error: allValid ? undefined : 'All values must be valid positive numbers',
          };
      }
    },
    transform: (value: string, variant: InputVariant = 'default') => {
      const values = value.split(',').map((v) => v.trim());
      return values.map((val) => {
        if (val === '') return;

        switch (variant) {
          case 'wei':
            return ethers.parseEther(val);
          default:
            return val === '' ? '' : ethers.toBigInt(val);
        }
      });
    },
  },
};
